import React, { useEffect, useRef, useState } from 'react';
import { Renderer, Camera, Transform, Texture, Program, Mesh } from 'ogl';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CustomEase } from 'gsap/CustomEase';

import { images, perspectives, cylinderConfig, particleConfig, imageConfig } from '../lib/data';
import {
    drawImageCover,
    getPositionClass,
    createCylinderGeometry,
    createParticleGeometry,
} from '../lib/utils';
import { cylinderVertex, cylinderFragment, particleVertex, particleFragment } from '../lib/shaders';
import './About.css';

gsap.registerPlugin(ScrollTrigger, CustomEase);

// Register custom eases
try {
    CustomEase.create('cinematicSilk', '0.45, 0.05, 0.55, 0.95');
    CustomEase.create('cinematicSmooth', '0.25, 0.1, 0.25, 1');
    CustomEase.create('cinematicFlow', '0.33, 0, 0.2, 1');
    CustomEase.create('cinematicLinear', '0.4, 0, 0.6, 1');
} catch (e) {
    console.warn("CustomEase registration failed/skipped", e);
}

const About = () => {
    const [isLoading, setIsLoading] = useState(true);
    const canvasRef = useRef(null);
    const containerRef = useRef(null); // The tall scroll container
    const textRefs = useRef([]);
    const rendererRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const cylinderRef = useRef(null);
    const cameraAnimRef = useRef({ x: 0, y: 0, z: 8, rotY: 0 });
    const particlesRef = useRef([]);
    const lastRotationRef = useRef(0);
    const velocityRef = useRef(0);
    const momentumRef = useRef(0);

    useEffect(() => {
        // Only initialize OGL if the refs are available
        if (!canvasRef.current || !containerRef.current) return;

        // --- 1. SETUP OGL RENDERER ---
        const renderer = new Renderer({
            canvas: canvasRef.current,
            width: window.innerWidth,
            height: window.innerHeight,
            dpr: Math.min(window.devicePixelRatio, 2),
            alpha: true,
            antialias: true,
        });
        const gl = renderer.gl;
        gl.clearColor(0, 0, 0, 1);
        gl.disable(gl.CULL_FACE);
        rendererRef.current = renderer;

        const getResponsiveDimensions = () => {
            const width = window.innerWidth;
            const isMobile = width < 768;
            const isTablet = width >= 768 && width < 1024;

            const maxRadius = isMobile ? 1.8 : isTablet ? 2.2 : 2.5;
            const cylinderHeight = isMobile ? 0.8 : isTablet ? 1.0 : 1.2;
            const cameraZ = isMobile ? 6 : isTablet ? 7 : 8;
            const fov = isMobile ? 50 : 45;

            return {
                cylinderScale: maxRadius / cylinderConfig.radius,
                cylinderHeight,
                cameraZ,
                fov,
                isMobile,
            };
        };

        const dimensions = getResponsiveDimensions();

        const camera = new Camera(gl, { fov: dimensions.fov });
        camera.position.set(0, 0, dimensions.cameraZ);
        cameraRef.current = camera;

        const scene = new Transform();
        sceneRef.current = scene;

        const geometry = createCylinderGeometry(gl, cylinderConfig);

        // --- 2. OPTIMIZATION FOR MOBILE TEXTURES ---
        const hardwareLimit = gl.getParameter(gl.MAX_TEXTURE_SIZE);
        const isMobileDevice = window.innerWidth < 768;
        const safeLimit = isMobileDevice ? 2048 : Math.min(hardwareLimit, 8192);

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', {
            willReadFrequently: false,
            alpha: false
        });
        const numImages = images.length;

        const totalWidthOriginal = imageConfig.width * numImages;
        const heightOriginal = imageConfig.height;

        const scale = Math.min(1, safeLimit / totalWidthOriginal);

        canvas.width = Math.floor(totalWidthOriginal * scale);
        canvas.height = Math.floor(heightOriginal * scale);

        let loadedImages = 0;
        const imageElements = [];

        const circumference = 2 * Math.PI * cylinderConfig.radius;
        const textureAspectRatio = imageConfig.height / (imageConfig.width * images.length);
        const idealHeight = circumference * textureAspectRatio;
        const heightCorrection = idealHeight / cylinderConfig.height;

        const animateLoop = () => {
            // We use GSAP ticker for the loop to sync with ScrollTrigger
            camera.position.set(cameraAnimRef.current.x, cameraAnimRef.current.y, cameraAnimRef.current.z);
            camera.lookAt([0, 0, 0]);

            if (cylinderRef.current) {
                const currentRotation = cylinderRef.current.rotation.y;
                velocityRef.current = currentRotation - lastRotationRef.current;
                lastRotationRef.current = currentRotation;

                const inertiaFactor = 0.15;
                const decayFactor = 0.92;
                momentumRef.current = momentumRef.current * decayFactor + velocityRef.current * inertiaFactor;

                const speed = Math.abs(velocityRef.current) * 100;
                const isRotating = Math.abs(velocityRef.current) > 0.0001;

                // Update particles
                particlesRef.current.forEach((particle) => {
                    const userData = particle.userData;
                    const targetOpacity = isRotating ? Math.min(speed * 3, 0.95) : 0;
                    const currentOpacity = particle.program.uniforms.uOpacity.value;
                    particle.program.uniforms.uOpacity.value = currentOpacity + (targetOpacity - currentOpacity) * 0.15;

                    if (isRotating) {
                        const rotationOffset = velocityRef.current * userData.speed * 1.5;
                        const newBaseAngle = userData.baseAngle + rotationOffset;
                        userData.baseAngle = newBaseAngle;

                        const segments = particleConfig.segments;
                        const positions = particle.geometry.attributes.position.data;

                        for (let j = 0; j <= segments; j++) {
                            const t = j / segments;
                            const angle = newBaseAngle + userData.angleSpan * t;
                            const radiusWithSpeed = userData.radius;
                            positions[j * 3] = Math.cos(angle) * radiusWithSpeed;
                            positions[j * 3 + 1] = userData.baseY;
                            positions[j * 3 + 2] = Math.sin(angle) * radiusWithSpeed;
                        }
                        particle.geometry.attributes.position.needsUpdate = true;
                    }
                });
            }
            renderer.render({ scene, camera });
        };

        const handleResize = () => {
            if (rendererRef.current && cameraRef.current && cylinderRef.current) {
                const newDimensions = getResponsiveDimensions();
                rendererRef.current.setSize(window.innerWidth, window.innerHeight);
                cameraRef.current.perspective({
                    fov: newDimensions.fov,
                    aspect: window.innerWidth / window.innerHeight,
                });
                cylinderRef.current.scale.set(
                    newDimensions.cylinderScale,
                    newDimensions.cylinderScale * heightCorrection,
                    newDimensions.cylinderScale
                );
            }
        };

        // --- 3. LOAD IMAGES AND BUILD SCENE ---
        images.forEach((imageSrc, index) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                imageElements[index] = img;
                loadedImages++;

                if (loadedImages === numImages) {
                    imageElements.forEach((img, i) => {
                        const xStartExact = (i / numImages) * canvas.width;
                        const xEndExact = ((i + 1) / numImages) * canvas.width;
                        const xPos = Math.floor(xStartExact);
                        const drawWidthActual = Math.floor(xEndExact) - xPos;
                        drawImageCover(ctx, img, xPos, 0, drawWidthActual, canvas.height);
                    });

                    const texture = new Texture(gl, {
                        wrapS: gl.CLAMP_TO_EDGE,
                        wrapT: gl.CLAMP_TO_EDGE,
                        minFilter: gl.LINEAR,
                        magFilter: gl.LINEAR,
                        generateMipmaps: false
                    });

                    texture.image = canvas;
                    texture.needsUpdate = true;

                    const program = new Program(gl, {
                        vertex: cylinderVertex,
                        fragment: cylinderFragment,
                        uniforms: {
                            tMap: { value: texture },
                            uDarkness: { value: 0.3 },
                        },
                        cullFace: null,
                    });

                    const cylinder = new Mesh(gl, { geometry, program });
                    cylinder.setParent(scene);
                    cylinder.rotation.y = 0.5;
                    cylinder.scale.set(dimensions.cylinderScale, dimensions.cylinderScale, dimensions.cylinderScale);
                    cylinderRef.current = cylinder;

                    setIsLoading(false);

                    // === INITIALIZE GSAP SCROLL TRIGGERS ===
                    const tl = gsap.timeline({
                        scrollTrigger: {
                            trigger: containerRef.current,
                            start: 'top top',
                            end: 'bottom bottom',
                            scrub: 1,
                        },
                    });

                    tl.to(cameraAnimRef.current, {
                        x: 0, y: 0, z: dimensions.cameraZ, duration: 1, ease: 'cinematicSilk',
                    })
                        .to(cameraAnimRef.current, {
                            x: 0, y: 5, z: 5, duration: 1, ease: 'cinematicFlow',
                        })
                        .to(cameraAnimRef.current, {
                            x: 1.5, y: 2, z: 2, duration: 2, ease: 'cinematicLinear',
                        })
                        .to(cameraAnimRef.current, {
                            x: 0.5, y: 0, z: 0.8, duration: 3.5, ease: 'power1.inOut',
                        })
                        .to(cameraAnimRef.current, {
                            x: -6, y: -1, z: dimensions.cameraZ, duration: 1, ease: 'cinematicSmooth',
                        });

                    tl.to(cylinderRef.current.rotation, {
                        y: '+=28.27',
                        duration: 8.5,
                        ease: 'none',
                    }, 0);

                    textRefs.current.forEach((textEl, index) => {
                        if (!textEl) return;
                        const sectionDuration = 100 / perspectives.length;
                        const start = index * sectionDuration;
                        const end = (index + 1) * sectionDuration;

                        const textTimeline = gsap.timeline({
                            scrollTrigger: {
                                trigger: containerRef.current,
                                start: `${start}% top`,
                                end: `${end}% top`,
                                scrub: 0.8,
                            },
                        });

                        textTimeline
                            .fromTo(textEl, { opacity: 0 }, { opacity: 1, duration: 0.2, ease: 'cinematicSmooth' })
                            .to(textEl, { opacity: 1, duration: 0.6, ease: 'none' })
                            .to(textEl, { opacity: 0, duration: 0.2, ease: 'cinematicSmooth' });
                    });

                    // Particles
                    for (let i = 0; i < particleConfig.numParticles; i++) {
                        const { geometry: lineGeometry, userData } = createParticleGeometry(gl, particleConfig, i, cylinderConfig.height);
                        const lineProgram = new Program(gl, {
                            vertex: particleVertex,
                            fragment: particleFragment,
                            uniforms: { uColor: { value: [1.0, 1.0, 1.0] }, uOpacity: { value: 0.0 } },
                            transparent: true, depthTest: true,
                        });
                        const particle = new Mesh(gl, { geometry: lineGeometry, program: lineProgram, mode: gl.LINE_STRIP });
                        particle.userData = userData;
                        particle.setParent(scene);
                        particlesRef.current.push(particle);
                    }

                    // Start loop
                    gsap.ticker.add(animateLoop);
                    window.addEventListener('resize', handleResize);
                }
            };
            img.src = imageSrc;
        });

        // --- CLEANUP ---
        return () => {
            window.removeEventListener('resize', handleResize);
            gsap.ticker.remove(animateLoop);
            ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
            // Optional: dispose OGL resources if needed, but simple page unmount is usually fine
        };
    }, []);

    return (
        <div className="about-page">
            <div className="about-canvas-container">
                <canvas ref={canvasRef} />
            </div>

            <div className="about-text-layer">
                {perspectives.map((perspective, index) => (
                    <div
                        key={index}
                        ref={(el) => { textRefs.current[index] = el; }}
                        className={`about-chapter ${getPositionClass(perspective.position)}`}
                    >
                        <h2>{perspective.title}</h2>
                        <p>{perspective.description}</p>
                    </div>
                ))}
            </div>

            <div className="scroll-indicator">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M12 5v14M19 12l-7 7-7-7" />
                </svg>
                <span>Scroll</span>
            </div>

            <div ref={containerRef} className="about-scroll-platform"></div>
        </div >
    );
};

export default About;
